/**
 * Organizations, user accounts, and the permission matrix.
 *
 * Access control is defined here, once, as data — then enforced in three places
 * that must not disagree: Firestore security rules, Cloud Functions, and the web
 * app's routing. The rules file is the real boundary (a client cannot bypass it);
 * the Functions check produces good error messages and audit entries; the UI check
 * merely avoids rendering a door that will not open. Deriving all three from this
 * one table is what keeps them consistent.
 */
import { z } from 'zod'
import { OrgTypeSchema, RoleSchema, type Role } from './vocab.js'
import { GeoPointSchema, IsoInstantSchema, idSchema } from './zod-util.js'
import type { OrgId, PatientId, SiteId, TrialId, UserId } from './ids.js'

/**
 * An organization. Both the tenancy boundary and the unit of trial ownership.
 *
 * A health system holds patient records; a sponsor holds trials. The same table
 * covers both because the interesting relationships are between them, and a single
 * `Org` with a `type` keeps the join simple while `type` gates what the org may do.
 */
export const OrgSchema = z.object({
  id: idSchema<OrgId>('org id'),
  name: z.string().min(1),
  type: OrgTypeSchema,
  /** Sites this org operates, for health systems and academic centers. */
  siteIds: z.array(idSchema<SiteId>('site id')).default([]),
  /** Trials this org sponsors or coordinates, for sponsors and CROs. */
  trialIds: z.array(idSchema<TrialId>('trial id')).default([]),
  city: z.string().default(''),
  state: z.string().default(''),
  geo: GeoPointSchema.optional(),
  createdAt: IsoInstantSchema,
})
export type Org = z.infer<typeof OrgSchema>

/**
 * A user account, mirroring the Firebase Auth UID.
 *
 * `role` and `orgId` are also written into the Auth custom claims, because
 * Firestore security rules can read claims but cannot afford a document read on
 * every rule evaluation. This document is the source of truth and a Cloud Function
 * syncs the claims from it — never the reverse, so a client that can somehow write
 * a claim still cannot grant itself a role.
 */
export const UserProfileSchema = z.object({
  id: idSchema<UserId>('user id'),
  email: z.string().email(),
  displayName: z.string().default(''),
  role: RoleSchema,
  /** The org this user belongs to. Absent only for `PATIENT`. */
  orgId: idSchema<OrgId>('org id').optional(),
  /**
   * For `PATIENT` accounts: the record this login *is*. Absent for staff.
   *
   * The authoritative link is `PatientIdentity.userId`, which points account-ward and
   * is what `firestore.rules` checks. That direction cannot be followed backwards from
   * a browser without querying `patientIdentities` — the most restricted collection in
   * the system — on every screen load just to learn which record is mine. So the link
   * is denormalized here, onto a document the user may already read, and the identity
   * document stays the source of truth for *authorization*: nothing is granted because
   * this field says so.
   */
  patientId: idSchema<PatientId>('patient id').optional(),
  /** Sites this user may act at. Empty means "all sites in the org". */
  siteIds: z.array(idSchema<SiteId>('site id')).default([]),
  /** For `CLINICIAN`/`INVESTIGATOR`: the trials they are an investigator on. */
  trialIds: z.array(idSchema<TrialId>('trial id')).default([]),
  npi: z.string().optional(),
  specialty: z.string().optional(),
  /** Whether the user has completed human-subjects research training. Gates recruitment actions. */
  hasHumanSubjectsTraining: z.boolean().default(false),
  disabled: z.boolean().default(false),
  createdAt: IsoInstantSchema,
  lastLoginAt: IsoInstantSchema.optional(),
})
export type UserProfile = z.infer<typeof UserProfileSchema>

/** The subset of the profile mirrored into Auth custom claims. Kept tiny — claims ride on every request. */
export const AuthClaimsSchema = z.object({
  role: RoleSchema,
  orgId: z.string().optional(),
  /** Truncated to 20 entries; beyond that, rules fall back to a document read. */
  siteIds: z.array(z.string()).max(20).default([]),
  trained: z.boolean().default(false),
})
export type AuthClaims = z.infer<typeof AuthClaimsSchema>

// ---------------------------------------------------------------------------
// Permissions
// ---------------------------------------------------------------------------

/**
 * Every distinct capability in the system.
 *
 * Named for the *action*, not the screen, so that adding a screen never requires a
 * new permission and a permission can never drift from what it actually guards.
 */
export const PERMISSIONS = [
  // patient records
  'patient:read:own', // a patient reading their own record
  'patient:read:panel', // a clinician reading patients on their care team
  'patient:read:org', // a coordinator reading their org's patients
  'patient:write:own',
  'patient:write:panel', // a clinician editing allow-listed clinical fields for a care-team patient
  'patient:search:identified', // search that returns names
  'patient:search:deidentified', // search that returns counts and pseudonyms only

  // matching
  'match:run:own',
  'match:run:panel',
  'match:run:org',
  'match:read:identified', // see the criterion table, which quotes the chart
  'match:read:deidentified', // see bands and counts only

  // candidates and referrals
  'candidate:shortlist',
  'candidate:clinician_review',
  'candidate:approve_outreach', // gated on human-subjects training
  'candidate:record_screening', // pre-screen, consent, enroll, screen-fail
  'candidate:express_interest', // the patient's own consent-to-contact action
  'candidate:create', // a clinician manually enrolling a patient in a trial, bypassing the matcher

  // trials
  'trial:read',
  'trial:write', // edit criteria, sites, slots
  'trial:publish',

  // feasibility
  'cohort:query', // aggregate counts across the network
  'cohort:export', // export de-identified aggregates

  // administration
  'org:manage',
  'user:manage',
  'audit:read',
  'model:evaluate', // read records for measuring the model, under MODEL_EVALUATION purpose
] as const

export type Permission = (typeof PERMISSIONS)[number]

/**
 * Role → permission matrix.
 *
 * Three properties of this table are load-bearing and deliberate:
 *
 *  1. **No sponsor-side role has any `:identified` permission.** `ORG_ANALYST`,
 *     `ORG_ADMIN`, and even `PLATFORM_ADMIN` are limited to de-identified reads.
 *     A sponsor's need is a count and a pseudonym; granting more would make the
 *     product's core privacy promise a matter of good intentions.
 *  2. **`ORG_COORDINATOR` is the only org role that can read identified data**,
 *     because a CRC works inside the care organization and is on the study team.
 *     This is the whole reason coordinators are modelled separately from analysts.
 *  3. **`candidate:approve_outreach` is additionally gated on
 *     `hasHumanSubjectsTraining`** (see `can`), not on role alone. Role says what
 *     job you do; training says whether you may contact a human subject.
 */
export const ROLE_PERMISSIONS: Record<Role, readonly Permission[]> = {
  PATIENT: [
    'patient:read:own',
    'patient:write:own',
    'match:run:own',
    'trial:read',
    'candidate:express_interest',
  ],

  CLINICIAN: [
    'patient:read:panel',
    'patient:write:panel',
    'patient:search:identified',
    'match:run:panel',
    'match:read:identified',
    'candidate:shortlist',
    'candidate:clinician_review',
    'candidate:create',
    'trial:read',
  ],

  INVESTIGATOR: [
    'patient:read:panel',
    'patient:read:org',
    'patient:search:identified',
    'match:run:panel',
    'match:run:org',
    'match:read:identified',
    'candidate:shortlist',
    'candidate:clinician_review',
    'candidate:approve_outreach',
    'candidate:record_screening',
    'candidate:create',
    'trial:read',
    'trial:write',
    'cohort:query',
  ],

  ORG_COORDINATOR: [
    'patient:read:org',
    'patient:search:identified',
    'match:run:org',
    'match:read:identified',
    'candidate:shortlist',
    'candidate:approve_outreach',
    'candidate:record_screening',
    'candidate:create',
    'trial:read',
    'cohort:query',
  ],

  ORG_ANALYST: ['patient:search:deidentified', 'match:read:deidentified', 'trial:read', 'cohort:query', 'cohort:export'],

  ORG_ADMIN: [
    'patient:search:deidentified',
    'match:read:deidentified',
    'trial:read',
    'trial:write',
    'trial:publish',
    'cohort:query',
    'cohort:export',
    'org:manage',
    'user:manage',
    'audit:read',
  ],

  PLATFORM_ADMIN: [
    'patient:search:deidentified',
    'match:read:deidentified',
    'trial:read',
    'trial:write',
    'trial:publish',
    'cohort:query',
    'cohort:export',
    'org:manage',
    'user:manage',
    'audit:read',
    'model:evaluate',
  ],
}

/** Permissions that additionally require completed human-subjects research training. */
export const TRAINING_GATED_PERMISSIONS: readonly Permission[] = [
  'candidate:approve_outreach',
  'candidate:record_screening',
]

/**
 * The single authority on "may this user do this".
 *
 * Used by the UI, by Cloud Functions, and mirrored by the Firestore rules. Takes
 * the whole subject rather than just a role so that the training gate and the
 * disabled flag are impossible to forget at a call site.
 */
export function can(
  subject: Pick<UserProfile, 'role' | 'disabled' | 'hasHumanSubjectsTraining'>,
  permission: Permission
): boolean {
  if (subject.disabled) return false
  if (!ROLE_PERMISSIONS[subject.role].includes(permission)) return false
  if (TRAINING_GATED_PERMISSIONS.includes(permission) && !subject.hasHumanSubjectsTraining) return false
  return true
}

/** Whether a role may ever see direct identifiers. Used to pick the sponsor-safe projection. */
export function seesIdentifiedData(role: Role): boolean {
  return ROLE_PERMISSIONS[role].includes('match:read:identified')
}

/**
 * Tenancy check: may this user touch data owned by `targetOrgId`?
 *
 * Separate from `can` because they answer different questions and both must pass.
 * `can` asks whether the role permits the verb; this asks whether the row is
 * theirs. A coordinator with every permission in the table still may not read
 * another health system's patients.
 */
export function inTenant(
  subject: Pick<UserProfile, 'role' | 'orgId'>,
  targetOrgId: string | undefined
): boolean {
  if (subject.role === 'PLATFORM_ADMIN') return true
  if (!targetOrgId) return false
  return subject.orgId === targetOrgId
}

/** Human-readable role labels for the UI. */
export const ROLE_LABELS: Record<Role, string> = {
  PATIENT: 'Patient',
  CLINICIAN: 'Clinician',
  INVESTIGATOR: 'Principal investigator',
  ORG_COORDINATOR: 'Study coordinator',
  ORG_ANALYST: 'Feasibility analyst',
  ORG_ADMIN: 'Trial organization admin',
  PLATFORM_ADMIN: 'Platform admin',
}

/** One-line description of what each role can do, shown on the demo sign-in screen. */
export const ROLE_DESCRIPTIONS: Record<Role, string> = {
  PATIENT: 'Search for trials, see which you may fit, and choose whether a study team may contact you.',
  CLINICIAN: 'See which of your patients match open trials, with the evidence behind each match.',
  INVESTIGATOR: 'Everything a clinician sees, plus your site’s candidate pipeline and trial setup.',
  ORG_COORDINATOR: 'Work the candidate pipeline for your site: review criteria, pre-screen, and track screening.',
  ORG_ANALYST: 'Test eligibility criteria against the network and see how many patients would qualify. Counts only — never identifiers.',
  ORG_ADMIN: 'Manage your organization’s trials, users, and feasibility analyses.',
  PLATFORM_ADMIN: 'Operate the platform: orgs, users, audit log, and model evaluation.',
}
